import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, ColorPicker, ColorPickerProps } from "antd";
import { I18N_NS } from "Config/i18n";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Editor, Transforms, Element, Text, Range } from "slate";
import { ReactEditor, useSlate } from "slate-react";
import { hasSelection } from "../utils/hasSelection";

interface TextColorProps {
  disabled?: boolean;
}

const setFontColor = (editor: Editor, color: string) => {
  const newProperties = { color: color || undefined };
  if (hasSelection(editor)) {
    // Apply font size to the selected text
    Transforms.setNodes(editor, newProperties, { at: editor.selection, match: (n) => Text.isText(n), split: true });
  }
  ReactEditor.focus(editor);
};

export const TextColor = ({ disabled }: TextColorProps) => {
  const { t } = useTranslation(I18N_NS.RICH_EDITOR);
  const editor = useSlate();
  const [color, setColor] = useState(null);
  const [open, setOpen] = useState(false);

  const handleChangeComplete: ColorPickerProps["onChangeComplete"] = (colorValue) => {
    const newColor = colorValue?.toHexString();
    // if color is without opacity, because opacity means that the "clear" was pressed
    if (newColor?.length <= 7) {
      const hex = colorValue.toHexString();
      setColor(hex);
      setFontColor(editor, hex);
      Editor.addMark(editor, "color", hex);
    } else {
      setColor(null);
      setFontColor(editor, null);
      Editor.removeMark(editor, "color");
    }
  };

  useEffect(() => {
    const selectedNodes = Array.from(
      Editor.nodes(editor, {
        at: editor.selection,
        match: (n) => !!(n as Element | Text).color,
      })
    );
    if (selectedNodes.length) {
      const currentColor = (selectedNodes[0][0] as Element)?.color as string;
      if (Range.isCollapsed(editor.selection) && color !== currentColor) {
        setColor(currentColor || null);
        if (currentColor) {
          Editor.addMark(editor, "color", currentColor);
        }
      }
    } else if (color) {
      setColor(null);
    }
  }, [editor?.selection]);

  return (
    <ColorPicker
      arrow={false}
      format="hex"
      value={color}
      onChangeComplete={handleChangeComplete}
      disabled={disabled}
      disabledAlpha
      open={open}
      panelRender={(panel) => (
        <div className="text-right">
          <Button
            className="mb-2"
            size="small"
            onClick={() => {
              setOpen(false);
              setFontColor(editor, null);
              setColor(null);
              Editor.removeMark(editor, "color");
            }}
          >
            {t("Reset")}
          </Button>
          {panel}
        </div>
      )}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
      }}
    >
      <FontAwesomeIcon
        title={t("Text color")}
        fixedWidth
        icon={["far", "font"]}
        color={color}
        className="pointer"
        onClick={() => setOpen(!open)}
      />
    </ColorPicker>
  );
};
